import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { CreateTrackRequest, Track } from "@/types";
import { createInitialFormData } from "@/utils/formHelpers";
import { useFormState } from "@/hooks/useFormState";
import { createCrudHooks } from "@/hooks/crudHooksFactory";
import i18n from "@/i18n/config";

export function useTracks(enabled = true) {
  return useQuery({
    queryKey: ["tracks"],
    queryFn: () => apiClient.getTracks(),
    enabled,
  });
}

export function useTrack(id: string) {
  return useQuery({
    queryKey: ["tracks", id],
    queryFn: () => apiClient.getTrack(id),
    enabled: !!id,
  });
}

export function useTrackLeaderboard(trackId: string) {
  return useQuery({
    queryKey: ["tracks", trackId, "leaderboard"],
    queryFn: () => apiClient.getTrackLeaderboard(trackId),
    enabled: !!trackId,
  });
}

const trackCrudHooks = createCrudHooks({
  create: {
    mutationFn: (trackData: CreateTrackRequest) =>
      apiClient.createTrack(trackData),
    cacheActions: [{ type: "invalidate", key: ["tracks"] }],
  },
  update: {
    mutationFn: ({
      id,
      ...trackData
    }: { id: string } & Partial<CreateTrackRequest>) =>
      apiClient.updateTrack(id, trackData),
    cacheActions: [
      { type: "invalidate", key: ["tracks"] },
      {
        type: "invalidate",
        key: (updatedTrack) => ["tracks", updatedTrack.id],
      },
    ],
  },
  delete: {
    mutationFn: (id: string) => apiClient.deleteTrack(id),
    cacheActions: [{ type: "invalidate", key: ["tracks"] }],
  },
});

export const useCreateTrack = trackCrudHooks.useCreate;
export const useUpdateTrack = trackCrudHooks.useUpdate;
export const useDeleteTrack = trackCrudHooks.useDelete;

// Validation logic extracted as a pure function for stable reference
function validateTrackData(
  data: Partial<CreateTrackRequest>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.name?.trim()) {
    errors.name = i18n.t("pages:tracks.validation.nameRequired");
  }

  if (!data.type) {
    errors.type = i18n.t("pages:tracks.validation.typeRequired");
  }

  if (data.latitude === undefined || isNaN(data.latitude)) {
    errors.latitude = i18n.t("pages:tracks.validation.latitudeRequired");
  } else if (data.latitude < -90 || data.latitude > 90) {
    errors.latitude = i18n.t("pages:tracks.validation.latitudeRange");
  }

  if (data.longitude === undefined || isNaN(data.longitude)) {
    errors.longitude = i18n.t("pages:tracks.validation.longitudeRequired");
  } else if (data.longitude < -180 || data.longitude > 180) {
    errors.longitude = i18n.t("pages:tracks.validation.longitudeRange");
  }

  if (data.description && data.description.length > 1000) {
    errors.description = i18n.t("pages:tracks.validation.descriptionTooLong");
  }

  return errors;
}

// Custom hook for managing track form state
export const useTrackForm = (initialTrack?: Track) => {
  const getInitialTrackData = useCallback(
    () =>
      createInitialFormData(initialTrack, {
        name: "",
        type: undefined as
          | "circuit"
          | "gymkhana"
          | "drag_strip"
          | "hill_climb"
          | "rally"
          | "oval"
          | "karting"
          | "other"
          | undefined,
        latitude: undefined as number | undefined,
        longitude: undefined as number | undefined,
        description: "",
        length: undefined as number | undefined,
        numberOfTurns: undefined as number | undefined,
        elevation: undefined as number | undefined,
        lapRecord: undefined as number | undefined,
        lapRecordHolder: "",
        yearOpened: undefined as number | undefined,
      }),
    [initialTrack],
  );

  const {
    data: trackData,
    errors,
    validate,
    updateField,
    resetForm,
    setData: setTrackData,
  } = useFormState(getInitialTrackData, validateTrackData);

  return {
    trackData,
    errors,
    validate,
    updateField,
    resetForm,
    setTrackData,
  };
};
